import { useQuery } from "@tanstack/react-query";
import { Card, Table, Descriptions, Typography, Spin, Alert } from "antd";
import type { ColumnsType } from "antd/es/table";
import type {
    PolicyHolderProfileResponse,
    PolicyHolderProfileType,
    PolicyHolderDependentProfile,
} from "../../types/participantsTypes";
import AmountFormat from "@/common/components/AmountFormat";
import StatusTag from "@/common/components/Status";
import FormatLocalDate from "@/common/components/FormatLocalDate";
import { get } from "@/common/services/httpClient";
import { API_ENDPOINTS, QUERY_KEYS } from "../../api/apiEndpoints";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

const ProfileWithDependents = () => {
    const { t } = useTranslation();

    const { data: profileData, isLoading, isError } = useQuery<PolicyHolderProfileResponse>({
        queryKey: QUERY_KEYS.PARTICIPANTS.GET_POLICYHOLDER_PROFILE,
        queryFn: async () => {
            const response = await get<PolicyHolderProfileResponse>(
                API_ENDPOINTS.PARTICIPANTS.GET_POLICYHOLDER_PROFILE
            );
            return response;
        },
    });

    const policyHolder: PolicyHolderProfileType | undefined = profileData?.data.policyHolder;
    const dependents: PolicyHolderDependentProfile[] = profileData?.data.dependents || [];

    const dependentColumns: ColumnsType<PolicyHolderDependentProfile> = [
        { title: t("Name"), dataIndex: "name", key: "name" },
        { title: t("Card Number"), dataIndex: "cardNumber", key: "cardNumber" },
        {
            title: t("DOB"),
            dataIndex: "dob",
            key: "dob",
            render: (dob: string) => <FormatLocalDate value={dob} format="DD-MM-YYYY" />,
        },
        { title: t("bloodGroup"), dataIndex: "bloodGroup", key: "bloodGroup" },
        {
            title: t("Coverage Amount"),
            dataIndex: "coverageAmount",
            key: "coverageAmount",
            render: (value: number) => (
                <AmountFormat value={value} currencyCode={policyHolder?.currency} />
            ),
        },
    ];

    if (isLoading)
        return (
            <div style={{ textAlign: "center", padding: 40 }}>
                <Spin size="large" tip={t("Loading...")} />
            </div>
        );

    if (isError) {
        return (
            <Alert
                type="error"
                message={t("Access Denied")}
                description={
                    profileData?.message ||
                    t("Sorry, you’re not authorized to view or modify this content.")
                }
                showIcon
                style={{ maxWidth: 600, margin: "40px auto" }}
            />
        );
    }

    if (!policyHolder) {
        return (
            <Alert
                type="info"
                message={t("No Profile Found")}
                description={t("No profile details were found for this user.")}
                showIcon
                style={{ maxWidth: 600, margin: "40px auto" }}
            />
        );
    }

    return (
        <div style={{ maxWidth: 900, margin: "24px auto", padding: "0 16px" }}>
            {policyHolder && (
                <>
                    <Card style={{ marginBottom: 24 }}>
                        {/* --- Header --- */}
                        <div style={{ marginBottom: 16 }}>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Title level={3} style={{ margin: 0 }}>
                                    <span>
                                        {policyHolder.name} <StatusTag status={policyHolder.status} />
                                    </span>
                                </Title>

                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <Text strong>{t("Balance")}:&nbsp;</Text>
                                    <AmountFormat
                                        value={policyHolder.balance}
                                        currencyCode={policyHolder.currency}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* --- Personal Info --- */}
                        <Descriptions bordered column={2} size="middle">
                            <Descriptions.Item label={t("Customer")}>
                                {policyHolder.customerName || t("Individual")}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Company")}>
                                {policyHolder.companyName || t("Individual")}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("NationalIDNo")}>
                                {policyHolder.nationalIdNumber}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Card Number")}>
                                {policyHolder.cardNumber}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("DOB")}>
                                <FormatLocalDate value={policyHolder.dob} format="DD-MM-YYYY" />
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Phone No")}>
                                {policyHolder.phone}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("bloodGroup")}>
                                {policyHolder.bloodGroup}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("area")}>
                                {policyHolder.area}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Country")}>
                                {policyHolder.country}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("City")}>
                                {policyHolder.city}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("address")}>
                                {policyHolder.address}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* --- Policy Info --- */}
                    <Card style={{ marginBottom: 24 }}>
                        <Title level={4}>{t("Plan Info")}</Title>
                        <Descriptions bordered column={2} size="middle">
                            <Descriptions.Item label={t("Policy Code")}>
                                {policyHolder.policyCode}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Category")}>
                                {policyHolder.category}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Plan")} span={2}>
                                <strong>{policyHolder.planName}</strong>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Coverage Amount")}>
                                <AmountFormat
                                    value={policyHolder.coverageAmount}
                                    currencyCode={policyHolder.currency}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Balance")}>
                                <AmountFormat
                                    value={policyHolder.balance}
                                    currencyCode={policyHolder.currency}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Inception Date")}>
                                <FormatLocalDate
                                    value={policyHolder.inceptionDate}
                                    format="DD-MM-YYYY"
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label={t("Expiry Date")}>
                                <FormatLocalDate
                                    value={policyHolder.expiryDate}
                                    format="DD-MM-YYYY"
                                />
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                </>
            )}

            {/* --- Dependents Table --- */}
            {dependents.length > 0 && (
                <Card>
                    <Title level={4}>{t("Dependent")}</Title>
                    <Table
                        dataSource={dependents}
                        columns={dependentColumns}
                        rowKey={(record) => record.cardNumber}
                        pagination={false}
                    />
                </Card>
            )}
        </div>
    );
};

export default ProfileWithDependents;
